import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import api from "../../../services/api";
import "./CAFView.css";
import { ArrowLeft } from "lucide-react";

const CAFView = () => {
  const { cafId } = useParams();
  const [cafData, setCafData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [role, setRole] = useState("project_associate"); // Default role
  const navigate = useNavigate();

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData && userData.role) {
        setRole(userData.role);
      }
    } catch (err) {
      console.error("Error getting user role from localStorage:", err);
    }

    const fetchCafData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/caf/${cafId}`);
        if (response.data && response.data.success) {
          setCafData(response.data.data.content);
        } else {
          setError("Failed to load CAF data");
        }
      } catch (err) {
        console.error("Error fetching CAF data:", err);
        setError(err.response?.data?.message || "Failed to load CAF data");
      } finally {
        setLoading(false);
      }
    };

    if (cafId) {
      fetchCafData();
    }
  }, [cafId]);

  if (loading) {
    return (
      <div className="caf-view-container">
        <Navbar role={role} />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading CAF data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="caf-view-container">
        <Navbar role={role} />
        <div className="error-container">
          <p className="error-message">Error: {error}</p>
          <button onClick={() => navigate(-1)} className="back-button">
            <ArrowLeft size={18} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!cafData) {
    return (
      <div className="caf-view-container">
        <Navbar role={role} />
        <div className="error-container">
          <p className="error-message">No CAF data found</p>
          <button onClick={() => navigate(-1)} className="back-button">
            <ArrowLeft size={18} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="caf-view-container">
      <Navbar role={role} />

      <div className="caf-view-header">
        <button onClick={() => navigate(-1)} className="back-button">
          <ArrowLeft size={18} />
          Back
        </button>
        <h1>Common Application Form</h1>
        <div className="project-name">{cafData.projectDetails.projectName}</div>
      </div>

      <div className="caf-view-content">
        {/* Project Details Section */}
        <section className="caf-view-section">
          <h2>Project Details</h2>
          <div className="caf-view-grid">
            <div className="caf-view-field">
              <label>Project Name</label>
              <p>{cafData.projectDetails.projectName || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Project Proposal</label>
              <p>{cafData.projectDetails.projectProposal || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Project ID</label>
              <p>{cafData.projectDetails.projectId || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Description</label>
              <p>{cafData.projectDetails.description || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Legacy Status</label>
              <p>{cafData.projectDetails.legacyStatus || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Company Name</label>
              <p>{cafData.projectDetails.companyName || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Address</label>
              <p>{cafData.projectDetails.address || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>City</label>
              <p>{cafData.projectDetails.city || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>State</label>
              <p>{cafData.projectDetails.state || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>District</label>
              <p>{cafData.projectDetails.district || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Pincode</label>
              <p>{cafData.projectDetails.pincode || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Email</label>
              <p>{cafData.projectDetails.email || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Phone Number</label>
              <p>{cafData.projectDetails.phoneNumber || "N/A"}</p>
            </div>
          </div>
        </section>

        {/* Project Location Section */}
        <section className="caf-view-section">
          <h2>Project Location</h2>
          <div className="caf-view-grid">
            <div className="caf-view-field">
              <label>International Border</label>
              <p>{cafData.projectLocation.internationalBorder || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Project Shape</label>
              <p>{cafData.projectLocation.projectShape || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Non-Forest Land</label>
              <p>{cafData.projectLocation.nonForestLand || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Forest Land</label>
              <p>{cafData.projectLocation.forestLand || "N/A"}</p>
            </div>
            <div className="caf-view-field">
              <label>Total Land</label>
              <p>{cafData.projectLocation.totalLand || "N/A"}</p>
            </div>
            {cafData.projectLocation.kmlFilePath && (
              <div className="caf-view-field full-width">
                <label>KML File</label>
                <a
                  href={`/api/files/${cafData.projectLocation.kmlFilePath}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="file-link"
                >
                  View KML File
                </a>
              </div>
            )}
          </div>
        </section>

        {/* Project Cost Section */}
        <section className="caf-view-section">
          <h2>Project Cost & Employment Details</h2>
          <div className="caf-view-grid">
            <div className="caf-view-field full-width">
              <label>Total Cost of the Project (in Lakhs)</label>
              <p>{cafData.projectCost.totalCost || "N/A"}</p>
            </div>

            <div className="caf-view-subsection full-width">
              <h3>During Construction Phase</h3>
              <div className="caf-view-grid">
                <div className="caf-view-field">
                  <label>Permanent Employees</label>
                  <p>
                    {cafData.projectCost.constructionPermanentEmployees ||
                      "N/A"}
                  </p>
                </div>
                <div className="caf-view-field">
                  <label>Employment Period (days)</label>
                  <p>
                    {cafData.projectCost.constructionEmploymentPeriod || "N/A"}
                  </p>
                </div>
                <div className="caf-view-field">
                  <label>Man-days</label>
                  <p>{cafData.projectCost.constructionManDays || "N/A"}</p>
                </div>
                <div className="caf-view-field">
                  <label>Temporary Employment</label>
                  <p>
                    {cafData.projectCost.constructionTemporaryEmployment ||
                      "N/A"}
                  </p>
                </div>
                <div className="caf-view-field">
                  <label>Total Employment</label>
                  <p>{cafData.projectCost.constructionTotal || "N/A"}</p>
                </div>
              </div>
            </div>

            <div className="caf-view-subsection full-width">
              <h3>During Operation Phase</h3>
              <div className="caf-view-grid">
                <div className="caf-view-field">
                  <label>Permanent Employees</label>
                  <p>
                    {cafData.projectCost.operationPermanentEmployees || "N/A"}
                  </p>
                </div>
                <div className="caf-view-field">
                  <label>Employment Period (days)</label>
                  <p>
                    {cafData.projectCost.operationEmploymentPeriod || "N/A"}
                  </p>
                </div>
                <div className="caf-view-field">
                  <label>Man-days</label>
                  <p>{cafData.projectCost.operationManDays || "N/A"}</p>
                </div>
                <div className="caf-view-field">
                  <label>Temporary Employment</label>
                  <p>
                    {cafData.projectCost.operationTemporaryEmployment || "N/A"}
                  </p>
                </div>
                <div className="caf-view-field">
                  <label>Total Employment</label>
                  <p>{cafData.projectCost.operationTotal || "N/A"}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Other Section */}
        <section className="caf-view-section">
          <h2>Other Details</h2>
          <div className="caf-view-grid">
            <div className="caf-view-field">
              <label>Is R&R Involved?</label>
              <p>{cafData.OtherSection.isRRInvolved || "N/A"}</p>
            </div>

            {cafData.OtherSection.isRRInvolved === "yes" && (
              <>
                <div className="caf-view-field">
                  <label>Number of Villages</label>
                  <p>{cafData.OtherSection.numVillages || "N/A"}</p>
                </div>
                <div className="caf-view-field">
                  <label>Number of Displaced Families</label>
                  <p>{cafData.OtherSection.numDisplacedFamilies || "N/A"}</p>
                </div>
                <div className="caf-view-field">
                  <label>Number of Affected Families</label>
                  <p>{cafData.OtherSection.numAffectedFamilies || "N/A"}</p>
                </div>
                <div className="caf-view-field">
                  <label>Rehabilitation Status</label>
                  <p>{cafData.OtherSection.rehabilitationStatus || "N/A"}</p>
                </div>
              </>
            )}

            <div className="caf-view-field">
              <label>Site Shifting Required?</label>
              <p>{cafData.OtherSection.siteShifting || "N/A"}</p>
            </div>

            {cafData.OtherSection.siteShifting === "yes" && (
              <div className="caf-view-field full-width">
                <label>Site Shifting Details</label>
                <p>{cafData.OtherSection.siteShiftingDetails || "N/A"}</p>
              </div>
            )}

            <div className="caf-view-field">
              <label>Alternative Sites Examined?</label>
              <p>{cafData.OtherSection.alternativeSites || "N/A"}</p>
            </div>

            {cafData.OtherSection.alternativeSites === "yes" && (
              <>
                <div className="caf-view-field full-width">
                  <label>Alternative Site Details</label>
                  <p>{cafData.OtherSection.alternativeSiteDetails || "N/A"}</p>
                </div>
                <div className="caf-view-field full-width">
                  <label>Site Justification</label>
                  <p>{cafData.OtherSection.siteJustification || "N/A"}</p>
                </div>
              </>
            )}

            {/* Display other file attachments */}
            {Object.entries({
              alternativeKMLFile: "Alternative KML File",
              governmentOrderFile: "Government Order File",
              litigationDocuments: "Litigation Documents",
              courtDirectionsFile: "Court Directions File",
              violationDocuments: "Violation Documents",
            }).map(
              ([key, label]) =>
                cafData.OtherSection[key] && (
                  <div className="caf-view-field" key={key}>
                    <label>{label}</label>
                    <a
                      href={`/api/files/${cafData.OtherSection[key]}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="file-link"
                    >
                      View File
                    </a>
                  </div>
                )
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default CAFView;
